/**
 * Unit conversion utilities for HealthIQ application
 * Supports both International (SI) and Imperial measurement systems
 */

// Unit system constants
export const UNIT_SYSTEMS = {
  INTERNATIONAL: 'international',
  IMPERIAL: 'imperial'
};

// Available units for different measurements
export const UNITS = {
  WEIGHT: {
    [UNIT_SYSTEMS.INTERNATIONAL]: 'kg',
    [UNIT_SYSTEMS.IMPERIAL]: 'lb'
  },
  HEIGHT: {
    [UNIT_SYSTEMS.INTERNATIONAL]: 'cm',
    [UNIT_SYSTEMS.IMPERIAL]: 'ft'
  },
  CIRCUMFERENCE: {
    [UNIT_SYSTEMS.INTERNATIONAL]: 'cm',
    [UNIT_SYSTEMS.IMPERIAL]: 'in'
  },
  BLOOD_GLUCOSE: {
    [UNIT_SYSTEMS.INTERNATIONAL]: 'mmol/L',
    [UNIT_SYSTEMS.IMPERIAL]: 'mg/dL'
  }
};

// Conversion factors
const CONVERSION_FACTORS = {
  WEIGHT: {
    kg_to_lb: 2.20462,
    lb_to_kg: 0.453592
  },
  HEIGHT: {
    cm_to_m: 0.01,
    m_to_cm: 100,
    cm_to_in: 0.393701,
    in_to_cm: 2.54,
    cm_to_ft: 0.0328084,
    ft_to_cm: 30.48
  },
  BLOOD_GLUCOSE: {
    mgdl_to_mmol: 0.0555,
    mmol_to_mgdl: 18.0182
  }
};

// Unit conversion functions
export const convertWeight = (value, fromUnit, toUnit) => {
  if (!value || isNaN(value)) return null;
  
  const from = fromUnit?.toLowerCase();
  const to = toUnit?.toLowerCase();
  
  // Convert to kg first if needed
  let kg = value;
  if (from === 'lb' || from === 'lbs' || from === 'pounds') {
    kg = value * CONVERSION_FACTORS.WEIGHT.lb_to_kg;
  } else if (from === 'stones' || from === 'st') {
    kg = value * 6.35029;
  }
  
  // Convert from kg to target unit
  if (to === 'lb' || to === 'lbs' || to === 'pounds') {
    return kg * CONVERSION_FACTORS.WEIGHT.kg_to_lb;
  } else if (to === 'stones' || to === 'st') {
    return kg / 6.35029;
  }
  
  return kg; // Already in kg
};

export const convertHeight = (value, fromUnit, toUnit) => {
  if (!value || isNaN(value)) return null;
  
  const from = fromUnit?.toLowerCase();
  const to = toUnit?.toLowerCase();
  
  // Convert to cm first
  let cm = value;
  if (from === 'm' || from === 'meters') {
    cm = value * CONVERSION_FACTORS.HEIGHT.m_to_cm;
  } else if (from === 'in' || from === 'inches') {
    cm = value * CONVERSION_FACTORS.HEIGHT.in_to_cm;
  } else if (from === 'ft' || from === 'feet') {
    cm = value * CONVERSION_FACTORS.HEIGHT.ft_to_cm;
  }
  
  // Convert from cm to target unit
  if (to === 'm' || to === 'meters') {
    return cm * CONVERSION_FACTORS.HEIGHT.cm_to_m;
  } else if (to === 'in' || to === 'inches') {
    return cm * CONVERSION_FACTORS.HEIGHT.cm_to_in;
  } else if (to === 'ft' || to === 'feet') {
    return cm * CONVERSION_FACTORS.HEIGHT.cm_to_ft;
  }
  
  return cm; // Already in cm
};

export const convertCircumference = (value, fromUnit, toUnit) => {
  if (!value || isNaN(value)) return null;
  
  const from = fromUnit?.toLowerCase();
  const to = toUnit?.toLowerCase();
  
  // Convert to cm first
  let cm = value;
  if (from === 'in' || from === 'inches') {
    cm = value * CONVERSION_FACTORS.HEIGHT.in_to_cm;
  }
  
  // Convert from cm to target unit
  if (to === 'in' || to === 'inches') {
    return cm * CONVERSION_FACTORS.HEIGHT.cm_to_in;
  }
  
  return cm; // Already in cm
};

export const convertBloodGlucose = (value, fromUnit, toUnit) => {
  if (!value || isNaN(value)) return null;
  
  const from = fromUnit?.toLowerCase();
  const to = toUnit?.toLowerCase();
  
  // Convert to mg/dL first
  let mgdl = value;
  if (from === 'mmol/l' || from === 'mmol') {
    mgdl = value * CONVERSION_FACTORS.BLOOD_GLUCOSE.mmol_to_mgdl;
  }
  
  // Convert from mg/dL to target unit
  if (to === 'mmol/l' || to === 'mmol') {
    return mgdl * CONVERSION_FACTORS.BLOOD_GLUCOSE.mgdl_to_mmol;
  }
  
  return mgdl; // Already in mg/dL
};

/**
 * Convert a measurement value to the user's preferred unit system
 * @param {number} value - The measurement value
 * @param {string} fromUnit - The original unit
 * @param {string} unitSystem - Target unit system (international/imperial)
 * @param {string} measurementType - Type of measurement (weight, height, circumference, blood_glucose)
 * @returns {object} - Converted value and display unit
 */
export const convertToUserUnit = (value, fromUnit, unitSystem, measurementType) => {
  if (!value || !fromUnit || !unitSystem || !measurementType) {
    return { value: null, unit: fromUnit };
  }
  
  let convertedValue = value;
  let targetUnit = UNITS[measurementType.toUpperCase()]?.[unitSystem];
  
  if (!targetUnit) {
    return { value, unit: fromUnit };
  }
  
  switch (measurementType.toLowerCase()) {
    case 'weight':
      convertedValue = convertWeight(value, fromUnit, targetUnit);
      break;
    case 'height':
      convertedValue = convertHeight(value, fromUnit, targetUnit);
      break;
    case 'circumference':
      convertedValue = convertCircumference(value, fromUnit, targetUnit);
      break;
    case 'blood_glucose':
      convertedValue = convertBloodGlucose(value, fromUnit, targetUnit);
      break;
    default:
      return { value, unit: fromUnit };
  }
  
  return { 
    value: convertedValue, 
    unit: targetUnit,
    // Format the value appropriately
    formattedValue: formatDisplayValue(convertedValue, targetUnit)
  };
};

/**
 * Format measurement values for display
 * @param {number} value - The measurement value
 * @param {string} unit - The unit
 * @returns {string} - Formatted value string
 */
export const formatDisplayValue = (value, unit) => {
  if (!value || isNaN(value)) return 'N/A';
  
  // Round to appropriate decimal places based on unit
  let decimals = 1;
  if (unit === 'lb' || unit === 'kg' || unit === 'cm') {
    decimals = 1;
  } else if (unit === 'ft' || unit === 'in') {
    decimals = 1;
  } else if (unit === 'mg/dL' || unit === 'mmol/L') {
    decimals = 1;
  }
  
  return `${parseFloat(value.toFixed(decimals))} ${unit}`;
};

/**
 * Calculate BMI with unit conversion
 * @param {number} weight - Weight value
 * @param {string} weightUnit - Weight unit
 * @param {number} height - Height value  
 * @param {string} heightUnit - Height unit
 * @returns {object} - BMI value and category
 */
export const calculateBMIWithUnits = (weight, weightUnit, height, heightUnit) => {
  if (!weight || !height) return { bmi: null, category: 'N/A' };
  
  // Convert to SI units (kg, cm)
  const weightInKg = convertWeight(weight, weightUnit, 'kg');
  const heightInCm = convertHeight(height, heightUnit, 'cm');
  
  if (!weightInKg || !heightInCm) return { bmi: null, category: 'N/A' };
  
  const heightInM = heightInCm / 100;
  const bmi = weightInKg / (heightInM * heightInM);
  
  let category = 'N/A';
  if (bmi < 18.5) category = 'Underweight';
  else if (bmi < 25) category = 'Normal';
  else if (bmi < 30) category = 'Overweight';
  else category = 'Obese';
  
  return { 
    bmi: parseFloat(bmi.toFixed(1)), 
    category 
  };
};

/**
 * Get appropriate unit for display based on measurement type and system
 * @param {string} measurementType - Type of measurement
 * @param {string} unitSystem - User's preferred unit system
 * @returns {string} - Display unit
 */
export const getDisplayUnit = (measurementType, unitSystem) => {
  return UNITS[measurementType.toUpperCase()]?.[unitSystem] || 'N/A';
};